import {FC, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {isMobile} from 'react-device-detect';
import {
  IDENTITY_VERIFICATION_PHOTOS_MAX_SIZE_MB,
  IV_DOCUMENT_IMAGE_TYPES,
  LivenessAction,
} from '@chekin/core';
import {useLivenessStore} from './store';
import {useLivenessDetection} from './hooks';
import {useModalControls} from '@chekin/guestapp-ui-kit';
import {useErrorModal} from '../../hooks';
import {useIVStore} from '../../store';
import selfieGif from '../../assets/images/selfie.gif?no-inline';
import {MobileWebcam} from './MobileWebcam';
import {FailedModal} from '../IVProcessModal';
import {LivenessInstructions} from './LivenessInstructions';
import {ChallengeProgressBar} from './ChallengeProgressBar';
import {Container, SpecialIndicator, VideoContainer, WebcamStyled} from './styled';

const videoConstraints = {
  facingMode: 'user',
  // width: {min: 320, ideal: 1280, max: 1920},
  // height: {min: 240, ideal: 720, max: 1080},
  width: {min: 320, ideal: 640, max: 800},
  height: {min: 240, ideal: 480, max: 600},
  frameRate: {ideal: 30, max: 30},
};

const webcamProps = {
  minScreenshotW: 800,
  minScreenshotH: 600,
  screenshotMaxSize: IDENTITY_VERIFICATION_PHOTOS_MAX_SIZE_MB,
  imageSmoothing: false,
  mirrored: true,
  roundedScanArea: true,
  additionalConstraints: videoConstraints,
};

const LIVENESS_TIMEOUT = 15000;

export type LivenessDetectionProps = {
  onComplete?: (data: {
    screenshot: string | null;
    recordingVideo: Blob | null;
    performedBy: 'CLIENT' | 'SERVER';
    completedActions: LivenessAction[];
  }) => void;
  disabled?: boolean;
  isRequired: boolean;
  onVerifyLater: () => void;
  forceLivenessMechanism?: 'AUTO' | 'SERVER' | 'CLIENT';
  className?: string;
  layout?: string;
};

export const LivenessDetection: FC<LivenessDetectionProps> = ({
  onComplete,
  className,
  disabled,
  layout,
  isRequired,
  onVerifyLater,
  forceLivenessMechanism = 'AUTO',
}) => {
  const {
    videoRef,
    isModelLoading,
    startLivenessDetection,
    faceScreenshot,
    isUsingClientDetection,
  } = useLivenessDetection({forceLivenessMechanism});
  const {t} = useTranslation('identity-verification');
  const isCompleteRef = useRef(false);

  const {openModal, closeModal, isOpen} = useModalControls();
  const currentStep = useLivenessStore(state => state.currentStep);
  const stepStatus = useLivenessStore(state => state.stepStatus);
  const recordingVideo = useLivenessStore(state => state.recordingVideo);
  const completedActions = useLivenessStore(state => state.completedActions);
  const performedBy = isUsingClientDetection ? 'CLIENT' : 'SERVER';
  const {setIsIvFailed} = useIVStore(state => state);
  const [isFailedLivenessTriggered, setIsFailedLivenessTriggered] = useState(false);
  const {displayError, ErrorModal} = useErrorModal();

  const shouldOpenModal = currentStep != 'COMPLETED' && !isModelLoading;

  useEffect(() => {
    if (isFailedLivenessTriggered) return;
    const timeout = setTimeout(() => {
      if (shouldOpenModal) {
        setIsFailedLivenessTriggered(true);
        openModal();
      }
    }, LIVENESS_TIMEOUT);

    return () => {
      clearTimeout(timeout);
    };
  }, [shouldOpenModal, openModal, isFailedLivenessTriggered]);

  const isComplete =
    !isCompleteRef.current &&
    currentStep === 'COMPLETED' &&
    stepStatus === 'success' &&
    onComplete &&
    !isFailedLivenessTriggered;

  useEffect(() => {
    if (isComplete) {
      onComplete({
        screenshot: faceScreenshot,
        recordingVideo,
        completedActions,
        performedBy,
      });
      isCompleteRef.current = true;
    }
  }, [
    isComplete,
    faceScreenshot,
    recordingVideo,
    onComplete,
    completedActions,
    performedBy,
  ]);

  const handleTryAgain = () => {
    setIsFailedLivenessTriggered(false);
    startLivenessDetection();
    closeModal();
    isCompleteRef.current = false;
  };

  return (
    <Container className={className}>
      <VideoContainer $status={stepStatus}>
        {!isUsingClientDetection && <SpecialIndicator />}
        {isMobile ? (
          <MobileWebcam
            status={stepStatus}
            onUserMedia={startLivenessDetection}
            ref={videoRef}
            isLoading={isModelLoading}
            disabled={!!disabled}
            children={!isUsingClientDetection && <SpecialIndicator />}
            onError={displayError}
            {...webcamProps}
          />
        ) : (
          <WebcamStyled
            onUserMedia={startLivenessDetection}
            ref={videoRef}
            isLoading={isModelLoading}
            onError={displayError}
            {...webcamProps}
          >
            <ChallengeProgressBar />
          </WebcamStyled>
        )}
      </VideoContainer>

      <LivenessInstructions />
      {isOpen && (
        <FailedModal
          open={isOpen}
          reasons={[t('liveness_detection.detection_failed_reasons')]}
          title={t('liveness_detection.detection_failed')}
          docType={IV_DOCUMENT_IMAGE_TYPES.selfie}
          onTryAgain={handleTryAgain}
          onClose={closeModal}
          image={selfieGif}
          layoutMode={layout}
          isRequired={isRequired}
          onVerifyLater={() => {
            setIsIvFailed(true);
            onVerifyLater();
            // navigate(`../../${PATHS.addPersonalDataForm}`);
          }}
        />
      )}
      <ErrorModal />
    </Container>
  );
};
